/**
 * ORB OTC Trading.
 */
import { useMemo, useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../api/client';
import { configAPI } from '../api/config';
import { otcAPI } from '../api/otc';
import { useAuthStore } from '../store/auth';
import { EmptyState } from '../components/ui/EmptyState';
import { ThemedPageHeader } from '../components/ui/ThemedPageHeader';
import { ThemedFixedTopBar, ThemedInput, ThemedField, ThemedPanel, ThemedStatusBadge } from '../components/ui/ThemedSurface';
import { OTC_COLORS as C, OTC_TABS, type OtcTabKey } from '@/constants';
import { UoHallCard, mapUoHallRowDisplay, type UoHallRow } from '../components/trading/UoHallCard';
import { getEpStatusLabel } from '../utils/ep-status';

interface OtcOrderRow {
  id: string;
  user_id: number;
  side: 'buy' | 'sell';
  amount_uo: string;
  unit_price: string;
  amount_ap: string;
  filled_uo: string;
  filled_ap: string;
  fee_ep: string;
  status: 'open' | 'partial' | 'done' | 'cancelled';
  cancel_reason: string | null;
  create_time: string;
}

interface OtcTradeRow {
  id: string;
  buy_order_id: string;
  sell_order_id: string;
  buyer_id: number;
  seller_id: number;
  amount_uo: string;
  unit_price: string;
  amount_ap: string;
  fee_ep_buyer: string;
  fee_ep_seller: string;
  create_time: string;
}

interface BookLevel {
  side: 'buy' | 'sell';
  unit_price: string;
  remaining_uo: string;
  orders: number;
}

interface ListResp<T> {
  total: number;
  rows: T[];
}

interface OtcHallRow extends UoHallRow {
  side: string;
  status: string;
  username?: string;
  nickname?: string;
}

function fmt(v: string, digits = 4): string {
  const n = Number(v);
  if (!Number.isFinite(n)) return v;
  return n.toLocaleString(undefined, { maximumFractionDigits: digits });
}

function TabStrip({ value, onChange }: { value: OtcTabKey; onChange: (tab: OtcTabKey) => void }) {
  const { t } = useTranslation();

  return (
    <div className="grid grid-cols-4 gap-1">
      {OTC_TABS.map((tab) => {
        const active = value === tab.key;
        return (
          <button
            key={tab.key}
            type="button"
            onClick={() => onChange(tab.key)}
            className="relative h-9 overflow-hidden rounded-xl px-1 text-xs font-semibold transition-all active:scale-[0.98]"
            style={{
              color: active ? C.gold : C.muted,
            }}
          >
            {active && <span className="absolute inset-x-3 bottom-0 h-px" style={{ background: C.gold }} />}
            <span className="relative block truncate">{t(tab.labelKey)}</span>
          </button>
        );
      })}
    </div>
  );
}

function FixedTabStrip({ value, onChange }: { value: OtcTabKey; onChange: (tab: OtcTabKey) => void }) {
  return (
    <ThemedFixedTopBar background={C.topbarBg} className="py-1">
      <TabStrip value={value} onChange={onChange} />
    </ThemedFixedTopBar>
  );
}

function SideToggle({ side, onChange }: { side: 'buy' | 'sell'; onChange: (side: 'buy' | 'sell') => void }) {
  const { t } = useTranslation();
  const items = [
    { key: 'buy' as const, label: t('otc.buyUo'), color: C.buy },
    { key: 'sell' as const, label: t('otc.sellUo'), color: C.sell },
  ];

  return (
    <div className="grid grid-cols-2 gap-2">
      {items.map((item) => {
        const active = side === item.key;
        return (
          <button
            key={item.key}
            type="button"
            onClick={() => onChange(item.key)}
            className="h-11 rounded-lg border text-sm font-semibold transition-all active:scale-[0.98]"
            style={{
              borderColor: active ? item.color : C.softBorder,
              background: active ? `${item.color}22` : C.inputBg,
              color: active ? item.color : C.muted,
            }}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}

function TicketHeader({ side }: { side: 'buy' | 'sell' }) {
  const { t } = useTranslation();
  const color = side === 'buy' ? C.buy : C.sell;

  return (
    <div className="flex items-start justify-between gap-3 border-b pb-3" style={{ borderColor: C.line }}>
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="h-5 w-1 rounded-full" style={{ background: color, boxShadow: `0 0 14px ${color}80` }} />
          <div className="truncate text-base font-semibold leading-none" style={{ color: C.text }}>
            {side === 'buy' ? t('otc.buyUo') : t('otc.sellUo')}
          </div>
        </div>
        <div className="mt-2 text-[10px]" style={{ color: C.muted }}>
          {t('otc.tabs.book')}
        </div>
      </div>
      <span className="shrink-0 rounded-full border px-3 py-1 text-[10px] font-semibold" style={{ borderColor: `${color}55`, background: `${color}18`, color }}>
        AP / ORB
      </span>
    </div>
  );
}

function EstimateStrip({ estimate }: { estimate: { totalAp: number; feeEp: number } | null }) {
  const { t } = useTranslation();

  return (
    <div className="grid grid-cols-2 gap-2">
      <SummaryMetric label={`${t('common.totalAmount')} (AP)`} value={estimate ? fmt(String(estimate.totalAp), 4) : '--'} color={C.amber} />
      <SummaryMetric label={`${t('common.fee')} (EP)`} value={estimate ? fmt(String(estimate.feeEp), 4) : '--'} color={C.text} />
    </div>
  );
}

function MiniSparkline({ color }: { color: string }) {
  const pts = '0,38 18,28 35,22 52,30 68,16 85,24 102,12 120,20';
  const gradientId = `spark-${color.replace('#', '')}`;

  return (
    <svg viewBox="0 0 120 48" width="100%" height="48" preserveAspectRatio="none">
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.45" />
          <stop offset="100%" stopColor={color} stopOpacity="0.02" />
        </linearGradient>
      </defs>
      <polygon points={`0,48 ${pts} 120,48`} fill={`url(#${gradientId})`} />
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function BookSummaryCard({ title, color, total, maxPrice }: { title: string; color: string; total: number; maxPrice: string }) {
  const { t } = useTranslation();
  const hasDepth = total > 0;

  return (
    <ThemedPanel theme={C} className="p-0">
      <div className="relative min-h-[136px] overflow-hidden p-3">
        <div className="absolute inset-0 opacity-80" style={{ background: `linear-gradient(135deg, ${color}24 0%, rgba(0,0,0,0) 48%)` }} />
        <div className="absolute -right-8 top-1 size-24 rounded-full blur-2xl" style={{ background: `${color}26` }} />
        <div className="relative z-10 flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="size-2 rounded-full shadow-[0_0_10px_currentColor]" style={{ color, background: color }} />
              <span className="truncate text-xs font-semibold" style={{ color }}>{title}</span>
            </div>
            <div className="mt-1 text-[10px]" style={{ color: C.muted }}>{hasDepth ? t('otc.totalQty') : t('common.noOrders')}</div>
          </div>
          <span className="rounded-full border px-2 py-0.5 text-[10px] font-semibold" style={{ borderColor: `${color}55`, background: `${color}18`, color }}>
            ORB
          </span>
        </div>

        <div className="relative z-10 mt-2 h-10 overflow-hidden rounded-lg border" style={{ borderColor: `${color}24`, background: 'rgba(0,0,0,0.20)' }}>
          <MiniSparkline color={color} />
        </div>

        <div className="relative z-10 mt-3 grid grid-cols-2 gap-2">
          <SummaryMetric label={`${t('otc.totalQty')} (ORB)`} value={hasDepth ? fmt(String(total), 2) : '0'} color={color} />
          <SummaryMetric label={`${t('otc.highPrice')} (AP)`} value={maxPrice} color={color} />
        </div>
      </div>
    </ThemedPanel>
  );
}

function SummaryMetric({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div className="min-w-0 rounded-lg border px-2 py-2" style={{ borderColor: C.line, background: C.inputBg }}>
      <div className="truncate text-[9px]" style={{ color: C.muted }}>{label}</div>
      <div className="mt-1 truncate font-mono text-[13px] font-semibold leading-none" style={{ color }}>{value}</div>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <div className="truncate text-[10px]" style={{ color: C.muted }}>{label}</div>
      <div className="mt-0.5 truncate font-mono text-sm font-semibold" style={{ color: C.text }}>{value}</div>
    </div>
  );
}



export function OtcTrading() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = (searchParams.get('tab') as OtcTabKey | null) ?? 'orders';
  const [tab, setTab] = useState<OtcTabKey>(
    OTC_TABS.some((x) => x.key === initialTab) ? initialTab : 'orders',
  );
  const [side, setSide] = useState<'buy' | 'sell'>('buy');
  const [amountUo, setAmountUo] = useState('');
  const [unitPrice, setUnitPrice] = useState('');
  const userId = useAuthStore((s) => s.profile?.id);
  const qc = useQueryClient();

  useEffect(() => {
    const next = searchParams.get('tab') as OtcTabKey | null;
    if (next && OTC_TABS.some((x) => x.key === next)) setTab(next);
  }, [searchParams]);

  const headerTitle =
    tab === 'orders' || tab === 'trades'
      ? t('uoHub.myOrders', '我的买卖')
      : t('otc.title');

  const { data: hall } = useQuery({
    queryKey: ['otc', 'hall'],
    queryFn: async () =>
      (await otcAPI.getHall({ page: 1, limit: 30, side: 'sell', status: 'done' })) as unknown as ListResp<OtcHallRow>,
    refetchInterval: 15_000,
    enabled: tab === 'hall',
  });

  const { data: book } = useQuery({
    queryKey: ['otc', 'book'],
    queryFn: () => otcAPI.getOrderBook({ depth: 10 }),
    refetchInterval: 5_000,
    enabled: tab === 'book',
  });

  const { data: feePct } = useQuery({
    queryKey: ['config', 'otc_fee_pct'],
    queryFn: () => configAPI.getConfig().then((r) => r.otc_fee_pct ?? '0'),
    staleTime: 60_000,
  });

  const { data: myOrders } = useQuery({
    queryKey: ['otc', 'orders', 'mine'],
    queryFn: async () =>
      (await otcAPI.getOrders({ page: 1, limit: 50 })) as unknown as ListResp<OtcOrderRow>,
    enabled: tab === 'orders',
    refetchInterval: 10_000,
  });

  const { data: myTrades } = useQuery({
    queryKey: ['otc', 'trades', 'mine'],
    queryFn: async () =>
      (await otcAPI.getTrades({ page: 1, limit: 50 })) as unknown as ListResp<OtcTradeRow>,
    enabled: tab === 'trades',
  });

  const place = useMutation({
    mutationFn: () =>
      otcAPI.placeOrder({ side, amount_uo: amountUo.trim(), unit_price: unitPrice.trim() }),
    onSuccess: () => { setAmountUo(''); setUnitPrice(''); qc.invalidateQueries({ queryKey: ['otc'] }); },
    onError: (err) => alert(err instanceof ApiError ? err.message : t('otc.orderFailed')),
  });

  const cancel = useMutation({
    mutationFn: (order_id: string) => otcAPI.cancelOrder({ order_id: Number(order_id) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['otc'] }),
    onError: (err) => alert(err instanceof ApiError ? err.message : t('otc.cancelFailed')),
  });

  const estimate = useMemo(() => {
    const a = Number(amountUo);
    const p = Number(unitPrice);
    if (!Number.isFinite(a) || !Number.isFinite(p) || a <= 0 || p <= 0) return null;
    const totalAp = a * p;
    const feeEp = totalAp * Number(feePct ?? '0');
    return { totalAp, feeEp };
  }, [amountUo, unitPrice, feePct]);

  const buyBook = Array.isArray(book?.buy) ? book.buy : [];
  const sellBook = Array.isArray(book?.sell) ? book.sell : [];
  const orderRows = Array.isArray(myOrders?.rows) ? myOrders.rows : [];
  const tradeRows = Array.isArray(myTrades?.rows) ? myTrades.rows : [];
  const buyTotal = buyBook.reduce((s, l) => s + (Number(l.quantity) || 0), 0);
  const sellTotal = sellBook.reduce((s, l) => s + (Number(l.quantity) || 0), 0);
  const hallRows = Array.isArray(hall?.rows) ? hall.rows : [];
  const hallStatusFallback = t('common.tradeDone', '交易完成');
  const hallLabels = useMemo(
    () => ({
      sellTotal: t('common.sellTotal', '挂卖总数'),
      filled: t('common.filled', '已成交'),
      coinPrice: t('uoTradingHall.coinPrice', '币价'),
      value: t('common.value', '价值'),
    }),
    [t],
  );
  const hallDisplayRows = useMemo(
    () => hallRows.map((row) => mapUoHallRowDisplay(row, hallStatusFallback)),
    [hallRows, hallStatusFallback],
  );
  const onOpenHall = useCallback(
    (routeId: string) => navigate(`/trading/hall/${routeId}`),
    [navigate],
  );

  return (
    <div className="relative min-h-full" style={{ background: C.pageBg }}>
      <div className="relative mx-auto min-h-full w-full max-w-[430px]">
        <ThemedPageHeader title={headerTitle} onBack={() => navigate('/trading')} theme={C} />

        <FixedTabStrip value={tab} onChange={setTab} />

        <div className="flex flex-col gap-4 overflow-y-auto px-5 pt-[115px]" style={{ height: 'calc(100% - 115px)', paddingBottom: '16px' }}>
          {tab === 'hall' && (
            <div className="flex flex-col">
              {hallDisplayRows.map((row) => (
                <UoHallCard key={row.id} row={row} labels={hallLabels} onOpen={onOpenHall} />
              ))}
              {hallDisplayRows.length === 0 && <EmptyState title={t('otc.hallEmpty')} />}
            </div>
          )}

          {tab === 'book' && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <BookSummaryCard title={t('otc.buyBook')} color={C.buy} total={buyTotal} maxPrice={buyBook[0]?.price ?? '--'} />
                <BookSummaryCard title={t('otc.sellBook')} color={C.sell} total={sellTotal} maxPrice={sellBook[0]?.price ?? '--'} />
              </div>
              <ThemedPanel theme={C} className="p-0">
                <div className="absolute inset-0 opacity-70" style={{ background: `radial-gradient(circle at 16% 0%, ${(side === 'buy' ? C.buy : C.sell)}22 0%, transparent 38%)` }} />
                <div className="relative z-10 flex flex-col gap-4 p-4">
                  <TicketHeader side={side} />
                  <SideToggle side={side} onChange={setSide} />

                  <div className="flex flex-col gap-4">
                    <ThemedField label={`${t('common.amount')} (ORB)`} required>
                      <div className="relative">
                        <ThemedInput
                          inputMode="decimal"
                          placeholder={side === 'buy' ? t('otc.enterBuyQty') : t('otc.enterSellQty')}
                          value={amountUo}
                          onChange={(e) => setAmountUo(e.target.value)}
                          className="pr-16"
                        />
                        <button
                          type="button"
                          onClick={() => { /* keep existing no-op */ }}
                          className="absolute right-2 top-1/2 flex h-7 -translate-y-1/2 items-center px-2 text-[11px] font-semibold transition-all active:scale-[0.98]"
                          style={{ color: C.amber }}
                        >
                          {t('common.all_btn')}
                        </button>
                      </div>
                    </ThemedField>
                    <ThemedField label={`${t('common.unitPrice')} (AP / ORB)`} required>
                      <ThemedInput
                        inputMode="decimal"
                        placeholder={t('otc.enterUnitPrice')}
                        value={unitPrice}
                        onChange={(e) => setUnitPrice(e.target.value)}
                      />
                    </ThemedField>
                  </div>

                  <EstimateStrip estimate={estimate} />

                  <button
                    type="button"
                    disabled={place.isPending || !amountUo.trim() || !unitPrice.trim()}
                    onClick={() => place.mutate()}
                    className="h-11 w-full rounded-xl border text-sm font-semibold transition-all hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60"
                    style={{ background: C.buttonBg, borderColor: C.buttonBorder, boxShadow: C.buttonShadow, color: C.text }}
                  >
                    {place.isPending ? '…' : side === 'buy' ? t('otc.confirmBuy') : t('otc.confirmSell')}
                  </button>
                </div>
              </ThemedPanel>
            </>
          )}

          {tab === 'orders' && (
            <div className="flex flex-col gap-3">
              {orderRows.map((row) => {
                const cancellable = row.status === 'open' || row.status === 'partial';
                const tone = row.side === 'buy' ? C.buy : C.sell;

                return (
                  <ThemedPanel theme={C} key={row.id} className="p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0 font-mono text-sm font-semibold" style={{ color: C.text }}>
                        #{row.id} <span style={{ color: tone }}>{row.side === 'buy' ? t('common.buy') : t('common.sell')}</span>
                      </div>
                      <ThemedStatusBadge label={getEpStatusLabel(row.status, t)} />
                    </div>
                    <div className="mt-3 grid grid-cols-3 gap-2">
                      <Metric label="ORB" value={fmt(row.amount_uo)} />
                      <Metric label={t('common.unitPrice')} value={`${fmt(row.unit_price)} AP`} />
                      <Metric label={t('common.totalAmount')} value={`${fmt(row.amount_ap)} AP`} />
                    </div>
                    <div className="mt-2 text-[10px]" style={{ color: C.muted }}>{row.create_time}</div>
                    {cancellable && row.user_id === userId && (
                      <button
                        type="button"
                        disabled={cancel.isPending}
                        onClick={() => cancel.mutate(row.id)}
                        className="mt-3 rounded-lg border px-4 py-1.5 text-xs font-semibold disabled:opacity-60"
                        style={{ borderColor: C.softBorder, background: C.inputBg, color: C.muted }}
                      >
                        {t('otc.cancelOrder')}
                      </button>
                    )}
                  </ThemedPanel>
                );
              })}
              {orderRows.length === 0 && <EmptyState title={t('common.noOrders')} />}
            </div>
          )}

          {tab === 'trades' && (
            <div className="flex flex-col gap-3">
              {tradeRows.map((row) => {
                const iAmBuyer = row.buyer_id === userId;
                const tone = iAmBuyer ? C.buy : C.sell;

                return (
                  <ThemedPanel theme={C} key={row.id} className="p-4">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm font-semibold" style={{ color: tone }}>
                        {iAmBuyer ? t('common.buy') : t('common.sell')} {fmt(row.amount_uo)} ORB
                      </span>
                      <span className="text-[10px]" style={{ color: C.muted }}>{row.create_time}</span>
                    </div>
                    <div className="mt-3 grid grid-cols-3 gap-2">
                      <Metric label={t('otc.tradePrice')} value={`${fmt(row.unit_price)} AP`} />
                      <Metric label={t('common.totalAmount')} value={`${fmt(row.amount_ap)} AP`} />
                      <Metric label={t('common.fee')} value={`${fmt(iAmBuyer ? row.fee_ep_buyer : row.fee_ep_seller)} EP`} />
                    </div>
                  </ThemedPanel>
                );
              })}
              {tradeRows.length === 0 && <EmptyState title={t('otc.noTrades')} />}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
